import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { safeJson } from '../utils/safeJson';
import { OwnerControlsBanner } from '../components/AdminRouteGuard';
import FeaturedContentManager from '../components/admin/FeaturedContentManager';
import AutoTrendingManager from '../components/admin/AutoTrendingManager';
import ContentManagementPanel from './admin/ContentManagementPanel';
import AnalyticsPanel from './admin/AnalyticsPanel';
import {
  Shield,
  Users,
  Settings,
  FileText,
  Swords,
  Star,
  AlertTriangle,
  Activity,
  Search,
  RefreshCw,
  UserPlus,
  UserMinus,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  Eye,
  EyeOff,
  Trash2,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  DollarSign,
  MessageCircle,
  Upload,
  Radio,
  UserCheck,
  History,
  Download,
  Filter,
  LayoutDashboard,
  Loader2,
  Megaphone,
  Image,
  Video,
  Zap,
  ArrowUp,
  ArrowDown,
  Music,
  Mic2,
  BarChart3,
  Globe,
  TrendingUp,
  UserX
} from 'lucide-react';

// Staff role badges
const StaffRoleBadge = ({ role }) => {
  const roleConfig = {
    owner: { color: 'bg-purple-500', label: 'Owner' },
    admin: { color: 'bg-red-500', label: 'Admin' },
    brand_admin: { color: 'bg-indigo-500', label: 'Brand Admin' },
    moderator: { color: 'bg-blue-500', label: 'Moderator' },
    featured_content_manager: { color: 'bg-green-500', label: 'Content Manager' },
    battle_host: { color: 'bg-orange-500', label: 'Battle Host' }
  };
  
  const config = roleConfig[role] || { color: 'bg-gray-500', label: role };
  
  return (
    <Badge className={`${config.color} text-foreground text-xs`}>
      {config.label}
    </Badge>
  );
};

// Audit action badge config
const getAuditActionBadge = (action) => {
  const config = {
    user_banned:       { icon: Ban, color: 'text-red-500', bg: 'bg-red-500/20', badgeClass: 'bg-red-500/20 text-red-400', label: 'User Banned' },
    user_unbanned:     { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/20', badgeClass: 'bg-green-500/20 text-green-400', label: 'User Unbanned' },
    user_suspended:    { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/20', badgeClass: 'bg-yellow-500/20 text-yellow-400', label: 'User Suspended' },
    user_soft_deleted: { icon: UserX, color: 'text-red-400', bg: 'bg-red-500/20', badgeClass: 'bg-red-500/20 text-red-400', label: 'User Deleted' },
    user_restored:     { icon: RotateCcw, color: 'text-green-400', bg: 'bg-green-500/20', badgeClass: 'bg-green-500/20 text-green-400', label: 'User Restored' },
    user_purged:       { icon: Trash2, color: 'text-red-600', bg: 'bg-red-600/20', badgeClass: 'bg-red-600/20 text-red-500', label: 'User Purged' },
    user_deleted:      { icon: Trash2, color: 'text-red-500', bg: 'bg-red-500/20', badgeClass: 'bg-red-500/20 text-red-400', label: 'User Deleted' },
    user_tier_changed: { icon: ArrowUp, color: 'text-purple-500', bg: 'bg-purple-500/20', badgeClass: 'bg-purple-500/20 text-purple-400', label: 'Tier Changed' },
    user_role_changed: { icon: UserCheck, color: 'text-blue-500', bg: 'bg-blue-500/20', badgeClass: 'bg-blue-500/20 text-blue-400', label: 'Role Changed' },
    staff_role_granted:{ icon: Crown, color: 'text-purple-500', bg: 'bg-purple-500/20', badgeClass: 'bg-purple-500/20 text-purple-400', label: 'Staff Role Granted' },
    staff_role_revoked:{ icon: UserMinus, color: 'text-orange-500', bg: 'bg-orange-500/20', badgeClass: 'bg-orange-500/20 text-orange-400', label: 'Staff Role Revoked' },
    content_deleted:   { icon: Trash2, color: 'text-orange-500', bg: 'bg-orange-500/20', badgeClass: 'bg-orange-500/20 text-orange-400', label: 'Content Deleted' },
    content_hidden:    { icon: EyeOff, color: 'text-yellow-500', bg: 'bg-yellow-500/20', badgeClass: 'bg-yellow-500/20 text-yellow-400', label: 'Content Hidden' },
    content_restored:  { icon: Eye, color: 'text-green-500', bg: 'bg-green-500/20', badgeClass: 'bg-green-500/20 text-green-400', label: 'Content Restored' },
    platform_toggle_changed: { icon: Settings, color: 'text-blue-500', bg: 'bg-blue-500/20', badgeClass: 'bg-blue-500/20 text-blue-400', label: 'Toggle Changed' },
  };
  return config[action] || { icon: Activity, color: 'text-primary', bg: 'bg-primary/20', badgeClass: 'bg-primary/20 text-primary', label: action?.replace(/_/g, ' ') || 'Unknown' };
};

// Confirmation Modal
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, isDangerous }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50" data-testid="confirm-modal">
      <Card className="p-6 max-w-md w-full mx-4">
        <div className="flex items-start gap-3 mb-4">
          {isDangerous ? (
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
          ) : (
            <Shield className="w-6 h-6 text-primary flex-shrink-0" />
          )}
          <div>
            <h3 className="font-bold text-lg">{title}</h3>
            <p className="text-muted-foreground text-sm mt-1">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            variant={isDangerous ? "destructive" : "default"} 
            onClick={onConfirm}
            data-testid="confirm-action-btn"
          >
            Confirm
          </Button>
        </div>
      </Card>
    </div>
  );
};

// Maintenance Tools Component - Platform Owner Only
const MaintenanceTools = ({ getAuthHeaders }) => {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});

  const runMaintenance = async (endpoint, name) => {
    setLoading(prev => ({ ...prev, [name]: true }));
    setResults(prev => ({ ...prev, [name]: null }));
    
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || `${name} completed successfully`);
        setResults(prev => ({ ...prev, [name]: { success: true, data } }));
      } else {
        toast.error(data.detail || `${name} failed`);
        setResults(prev => ({ ...prev, [name]: { success: false, error: data.detail } }));
      }
    } catch (error) {
      toast.error(`Error running ${name}: ${error.message}`);
      setResults(prev => ({ ...prev, [name]: { success: false, error: error.message } }));
    } finally {
      setLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const tools = [
    {
      id: 'fix-urls',
      name: 'Fix Broken Media URLs',
      description: 'Fixes images and media that disappeared after updates. Converts absolute URLs to relative paths so media works across deployments.',
      endpoint: '/admin/fix-media-urls',
      icon: Image
    },
    {
      id: 'fix-posts',
      name: 'Fix Post Author Info',
      description: 'Fixes posts showing "@user" or "Artist" instead of actual author names. This updates all posts with missing author information.',
      endpoint: '/posts/admin/fix-author-info',
      icon: MessageCircle
    },
    {
      id: 'fix-durations',
      name: 'Recalculate Track Durations',
      description: 'Recalculates and updates duration for all direct upload tracks that show incorrect times (like 3:30 for all songs).',
      endpoint: '/tracks/admin/recalculate-durations',
      icon: Clock
    }
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <RotateCcw className="w-6 h-6 text-purple-500" />
        <div>
          <h2 className="text-xl font-bold">Data Maintenance Tools</h2>
          <p className="text-sm text-muted-foreground">Fix data issues and run migrations</p>
        </div>
      </div>

      <div className="space-y-4">
        {tools.map(tool => {
          const Icon = tool.icon;
          const isLoading = loading[tool.id];
          const result = results[tool.id];
          
          return (
            <div key={tool.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <Icon className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{tool.name}</h3>
                    <p className="text-sm text-muted-foreground">{tool.description}</p>
                  </div>
                </div>
                <Button 
                  onClick={() => runMaintenance(tool.endpoint, tool.id)}
                  disabled={isLoading}
                  variant="outline"
                  size="sm"
                  data-testid={`run-${tool.id}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Run
                    </>
                  )}
                </Button>
              </div>
              
              {result && (
                <div className={`p-3 rounded-lg text-sm ${result.success ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                  {result.success ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>
                        {result.data?.message || 'Completed successfully'}
                        {result.data?.updated_count !== undefined && (
                          <span className="ml-1">({result.data.updated_count} items updated)</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      <span>{result.error || 'Failed'}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-yellow-500">Important</p>
            <p className="text-muted-foreground">These tools modify database records. They are safe to run multiple times but may take a moment to complete on large datasets.</p>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Environment Debug Panel - Platform Owner Only
const EnvironmentDebugPanel = ({ getAuthHeaders }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDebug = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/env-debug`, { headers: getAuthHeaders() });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDebug(); }, []);

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return <p className="text-muted-foreground p-4">Failed to load environment data.</p>;

  const envColor = data.is_production ? 'text-red-400 bg-red-500/10 border-red-500/30' : 'text-green-400 bg-green-500/10 border-green-500/30';

  return (
    <Card className="p-6 space-y-6" data-testid="env-debug-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-blue-500" />
          <div>
            <h2 className="text-xl font-bold">Environment Debug</h2>
            <p className="text-sm text-muted-foreground">Verify DB, environment, and deployment status</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDebug} data-testid="refresh-env-debug">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Environment Banner */}
      <div className={`flex items-center gap-3 p-4 rounded-lg border ${envColor}`} data-testid="env-banner">
        <Activity className="w-5 h-5 flex-shrink-0" />
        <div>
          <p className="font-bold text-lg uppercase">{data.app_env}</p>
          <p className="text-sm opacity-80">{data.is_production ? 'PRODUCTION — Seeds disabled, destructive ops blocked' : 'Preview/Dev — Seeds enabled, test data allowed'}</p>
        </div>
      </div>

      {/* Key Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Database</p>
          <p className="font-mono font-bold text-sm" data-testid="debug-db-name">{data.db_name}</p>
        </div>
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Mongo Host</p>
          <p className="font-mono text-sm">{data.mongo_url_masked}</p>
        </div>
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Seeds Enabled</p>
          <p className={`font-bold text-sm ${data.seeds_enabled ? 'text-yellow-400' : 'text-green-400'}`}>
            {data.seeds_enabled ? 'YES (non-prod)' : 'NO (prod safe)'}
          </p>
        </div>
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Last Deploy</p>
          <p className="text-sm">{data.last_deploy ? new Date(data.last_deploy).toLocaleString() : 'N/A'}</p>
        </div>
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Deploy Count</p>
          <p className="font-bold text-sm">{data.deployment_count}</p>
        </div>
        <div className="border rounded-lg p-3 space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Migrations</p>
          <p className="font-bold text-sm">{data.migrations_applied} applied</p>
        </div>
      </div>

      {/* Data Counts */}
      <div>
        <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Live Data Counts</h3>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {Object.entries(data.data_counts).map(([key, val]) => (
            <div key={key} className="text-center border rounded-lg p-3">
              <p className="text-xl font-bold">{val}</p>
              <p className="text-xs text-muted-foreground capitalize">{key}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Migrations List */}
      {data.migrations?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-muted-foreground uppercase tracking-wider">Applied Migrations</h3>
          <div className="space-y-1">
            {data.migrations.map(m => (
              <div key={m.migration_id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/20">
                <span className="font-mono text-xs">{m.migration_id}</span>
                <span className="text-xs text-muted-foreground">{new Date(m.applied_at).toLocaleString()} ({m.app_env})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Production Readiness Checklist */}
      <div className="border rounded-lg p-4 space-y-2">
        <h3 className="font-semibold text-sm">Production Readiness</h3>
        <div className="space-y-1 text-sm">
          {[
            { label: 'APP_ENV set', ok: !!data.app_env },
            { label: 'DB is NOT test/dev in prod', ok: !data.is_production || !/(dev|test|preview)/i.test(data.db_name) },
            { label: 'Seeds disabled in prod', ok: !data.is_production || !data.seeds_enabled },
            { label: 'Data persists across deploys', ok: data.deployment_count > 1 },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              {c.ok ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
              <span className={c.ok ? 'text-green-400' : 'text-red-400'}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

// Promotions Manager Component - Platform Owner Only
const PromotionsManager = ({ getAuthHeaders }) => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addForm, setAddForm] = useState({ promo_type: 'spotlight', custom_caption: '', priority: 10 });

  const fetchPromos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/promotions`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setPromos(d.promotions || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPromos(); }, []);

  const searchUsers = async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(q)}&limit=8`, { headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); setSearchResults(d.users || d.results || []); }
    } catch (e) { console.error(e); }
    finally { setSearching(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const addPromotion = async (userId, userName) => {
    try {
      const res = await fetch(`${API}/admin/promotions`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ creator_user_id: userId, ...addForm })
      });
      if (res.ok) {
        toast.success(`Added ${userName} to promo row`);
        setSearchQuery('');
        setSearchResults([]);
        fetchPromos();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add promotion');
      }
    } catch (e) { toast.error('Failed to add promotion'); }
  };

  const deletePromo = async (promoId) => {
    try {
      const res = await fetch(`${API}/admin/promotions/${promoId}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { toast.success('Promotion removed'); fetchPromos(); }
    } catch (e) { toast.error('Failed to remove'); }
  };

  const updatePromo = async (promoId, updates) => {
    try {
      const res = await fetch(`${API}/admin/promotions/${promoId}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (res.ok) { fetchPromos(); }
    } catch (e) { toast.error('Failed to update'); }
  };

  const PROMO_TYPES = [
    { value: 'spotlight', label: 'Spotlight' },
    { value: 'live', label: 'Live' },
    { value: 'new_drop', label: 'New Drop' },
    { value: 'new_episode', label: 'New Episode' },
  ];

  return (
    <Card className="p-6 space-y-6" data-testid="promotions-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="w-6 h-6 text-secondary" />
          <div>
            <h2 className="text-xl font-bold">Promotions Manager</h2>
            <p className="text-sm text-muted-foreground">Manage the "Tune In Now" promo row on the landing page</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchPromos} data-testid="refresh-promos">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Add Creator */}
      <div className="border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><UserPlus className="w-4 h-4" /> Add Creator to Promo Row</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or handle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="promo-search-input"
            />
            {(searchResults.length > 0 || searching) && searchQuery.length >= 2 && (
              <div className="absolute z-20 top-full mt-1 left-0 right-0 bg-card border rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {searching ? (
                  <div className="p-3 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Searching...</div>
                ) : searchResults.map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => addPromotion(u.user_id, u.name)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors text-left"
                    data-testid={`promo-add-${u.handle || u.user_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold">{(u.name||'?')[0]}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.handle ? `@${u.handle}` : ""} · {u.role || 'user'}</p>
                    </div>
                    <UserPlus className="w-4 h-4 text-secondary flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <select
            value={addForm.promo_type}
            onChange={(e) => setAddForm(p => ({ ...p, promo_type: e.target.value }))}
            className="h-10 rounded-md border bg-background px-3 text-sm"
            data-testid="promo-type-select"
          >
            {PROMO_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            placeholder="Custom caption (optional)"
            value={addForm.custom_caption}
            onChange={(e) => setAddForm(p => ({ ...p, custom_caption: e.target.value }))}
            data-testid="promo-caption-input"
          />
          <Input
            type="number"
            placeholder="Priority (lower = first)"
            value={addForm.priority}
            onChange={(e) => setAddForm(p => ({ ...p, priority: parseInt(e.target.value) || 10 }))}
            min={1}
            max={100}
            data-testid="promo-priority-input"
          />
        </div>
      </div>

      {/* Current Promotions List */}
      <div>
        <h3 className="font-semibold text-sm mb-3 text-muted-foreground uppercase tracking-wider">
          Active Promotions ({promos.filter(p => p.active).length}) / Total ({promos.length})
        </h3>
        {loading ? (
          <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : promos.length === 0 ? (
          <div className="text-center p-8 border rounded-lg border-dashed">
            <Zap className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-muted-foreground">No promotions yet. Search for a creator above to add one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {promos.map((p, idx) => (
              <div
                key={p.promo_id}
                className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${p.active ? 'border-secondary/30 bg-secondary/5' : 'border-border opacity-60'}`}
                data-testid={`promo-item-${p.promo_id}`}
              >
                {/* Priority */}
                <div className="flex flex-col gap-1">
                  <button onClick={() => updatePromo(p.promo_id, { priority: Math.max(1, (p.priority || 10) - 1) })} className="hover:text-secondary"><ArrowUp className="w-3 h-3" /></button>
                  <span className="text-xs font-mono text-center w-5">{p.priority || '-'}</span>
                  <button onClick={() => updatePromo(p.promo_id, { priority: (p.priority || 10) + 1 })} className="hover:text-secondary"><ArrowDown className="w-3 h-3" /></button>
                </div>

                {/* Creator Info */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-secondary/20 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {p.creator_avatar ? <img src={p.creator_avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-xs font-bold">{(p.creator_name||p.creator_user_id||'?')[0]}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{p.creator_name || p.creator_user_id}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {p.creator_handle && <span className="text-xs text-muted-foreground">@{p.creator_handle}</span>}
                      <Badge variant="outline" className="text-xs">{p.promo_type}</Badge>
                      {p.custom_caption && <span className="text-xs text-muted-foreground truncate">"{p.custom_caption}"</span>}
                    </div>
                  </div>
                </div>

                {/* Toggle Active */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{p.active ? 'Active' : 'Off'}</span>
                  <Switch
                    checked={p.active}
                    onCheckedChange={(v) => updatePromo(p.promo_id, { active: v })}
                    data-testid={`promo-toggle-${p.promo_id}`}
                  />
                </div>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => deletePromo(p.promo_id)}
                  data-testid={`promo-delete-${p.promo_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

export default function AdminControlCenterPage() {
  const { user, getAuthHeaders, isOwner, isPlatformOwner } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Staff info
  const [myStaffInfo, setMyStaffInfo] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [staffRoles, setStaffRoles] = useState([]);
  
  // Dashboard stats
  const [stats, setStats] = useState(null);
  
  // Users
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userTotal, setUserTotal] = useState(0);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  
  // Platform toggles
  const [toggles, setToggles] = useState({});
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({ days: 7 });
  
  // Ad campaigns
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  
  // Modal state
  const [confirmModal, setConfirmModal] = useState({ isOpen: false });
  
  // Action forms
  const [actionEmail, setActionEmail] = useState('');
  const [selectedTier, setSelectedTier] = useState('pro');
  const [selectedRole, setSelectedRole] = useState('artist');
  const [selectedStaffRole, setSelectedStaffRole] = useState('moderator');
  const [suspendHours, setSuspendHours] = useState(24);

  // Check access - Platform owner bypasses API check
  useEffect(() => {
    if (isPlatformOwner) {
      // Platform owner has full access - skip API check
      setMyStaffInfo({
        is_staff: true,
        staff_role: 'owner',
        email: user?.email,
        permissions: ['*'] // All permissions
      });
      fetchDashboardStats();
      fetchStaffMembers();
      fetchStaffRoles();
      fetchPlatformToggles();
      setLoading(false);
    } else {
      fetchMyStaffInfo();
    }
  }, [isPlatformOwner]);

  const fetchMyStaffInfo = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/me`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setMyStaffInfo(data);
        if (!data.is_staff && !isPlatformOwner) {
          toast.error('Not authorized');
          navigate('/feed');
          return;
        }
        // Load initial data
        fetchDashboardStats();
        fetchStaffMembers();
        fetchStaffRoles();
        fetchPlatformToggles();
      } else {
        if (!isPlatformOwner) {
          toast.error('Not authorized');
          navigate('/feed');
        }
      }
    } catch (error) {
      if (!isPlatformOwner) {
        toast.error('Not authorized');
        navigate('/feed');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/dashboard-stats`, {
        headers: getAuthHeaders()
      });
      if (res.ok) setStats(await res.json());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchStaffMembers = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/staff-members`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStaffMembers(data.staff_members || []);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const fetchStaffRoles = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/staff-roles`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStaffRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      const res = await fetch(`${API}/admin/control-center/users?q=${encodeURIComponent(search)}&limit=50`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
        setUserTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDeletedUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/deleted-users`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setDeletedUsers(data.users || []);
      }
    } catch (error) {
      console.error('Failed to fetch deleted users:', error);
    }
  };

  const handleRestoreUser = async (userId, email) => {
    try {
      const res = await fetch(`${API}/admin/control-center/restore-user`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${email} restored`);
        fetchDeletedUsers();
        fetchUsers(userSearch);
        fetchDashboardStats();
      } else {
        toast.error(data.detail || 'Restore failed');
      }
    } catch (e) { toast.error('Restore failed'); }
  };

  const handlePurgeUser = async (userId, email) => {
    try {
      const res = await fetch(`${API}/admin/control-center/delete-user`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, confirm_phrase: 'DELETE USER', permanent: true })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${email} permanently deleted`);
        fetchDeletedUsers();
        fetchDashboardStats();
      } else {
        toast.error(data.detail || 'Purge failed');
      }
    } catch (e) { toast.error('Purge failed'); }
  };

  const fetchPlatformToggles = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/platform-toggles`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setToggles(data.toggles || {});
      }
    } catch (error) {
      console.error('Failed to fetch toggles:', error);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const res = await fetch(`${API}/admin/control-center/audit-logs?days=${auditFilter.days}&limit=100`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs || []);
        setAuditTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const fetchPendingCampaigns = async () => {
    try {
      const res = await fetch(`${API}/admin/ads/pending`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPendingCampaigns(data.campaigns || []);
        setCampaignsTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const handleApproveCampaign = async (campaignId) => {
    try {
      const res = await fetch(`${API}/admin/ads/${campaignId}/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Campaign approved');
        fetchPendingCampaigns();
        fetchAuditLogs();
      } else {
        toast.error(result.error || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve campaign');
    }
  };

  const handleRejectCampaign = async (campaignId) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      const res = await fetch(`${API}/admin/ads/${campaignId}/reject`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const result = await safeJson(res);
      if (result.ok) {
        toast.success('Campaign rejected');
        fetchPendingCampaigns();
        fetchAuditLogs();
      } else {
        toast.error(result.error || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject campaign');
    }
  };

  // Actions
  const handleAction = async (endpoint, body, successMessage) => {
    try {
      const res = await fetch(`${API}/admin/control-center/${endpoint}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      const result = await safeJson(res);
      if (result.ok) {
        toast.success(successMessage);
        fetchDashboardStats();
        fetchUsers(userSearch);
        fetchAuditLogs();
        setActionEmail('');
      } else {
        toast.error(result.error || 'Action failed');
      }
    } catch (error) {
      toast.error('Action failed');
    }
    setConfirmModal({ isOpen: false });
  };

  const handleToggleChange = async (toggleKey, enabled) => {
    try {
      const res = await fetch(`${API}/admin/control-center/platform-toggles`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ toggle: toggleKey, enabled })
      });
      
      const result = await safeJson(res);
      if (result.ok) {
        toast.success(`${toggleKey.replace(/_/g, ' ')} ${enabled ? 'enabled' : 'disabled'}`);
        fetchPlatformToggles();
        fetchAuditLogs();
      } else {
        toast.error(result.error || 'Failed to update toggle');
      }
    } catch (error) {
      toast.error('Failed to update toggle');
    }
  };

  const hasPermission = (permission) => {
    // Platform owner has ALL permissions
    if (isPlatformOwner) return true;
    return myStaffInfo?.permissions?.includes(permission);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/20 rounded-lg">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Admin Control Center</h1>
                <p className="text-sm text-muted-foreground">
                  Platform management dashboard
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {myStaffInfo && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{myStaffInfo.email}</span>
                  <StaffRoleBadge role={myStaffInfo.staff_role} />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => {
                fetchDashboardStats();
                fetchStaffMembers();
                fetchAuditLogs();
              }}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Private Owner Controls Banner - Only visible to platform owner */}
        <OwnerControlsBanner />
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap gap-1">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            {hasPermission('view_users') && (
              <TabsTrigger value="users" data-testid="tab-users" onClick={() => fetchUsers('')}>
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
            )}
            {hasPermission('assign_staff_role') && (
              <TabsTrigger value="staff" data-testid="tab-staff">
                <Crown className="w-4 h-4 mr-2" />
                Staff
              </TabsTrigger>
            )}
            {hasPermission('manage_platform_toggles') && (
              <TabsTrigger value="toggles" data-testid="tab-toggles">
                <Settings className="w-4 h-4 mr-2" />
                Platform Toggles
              </TabsTrigger>
            )}
            {hasPermission('manage_featured') && (
              <TabsTrigger value="ads" data-testid="tab-ads" onClick={fetchPendingCampaigns}>
                <Megaphone className="w-4 h-4 mr-2" />
                Ads
              </TabsTrigger>
            )}
            {hasPermission('view_reports') && (
              <TabsTrigger value="moderation" data-testid="tab-moderation">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Moderation
              </TabsTrigger>
            )}
            {hasPermission('view_audit_logs') && (
              <TabsTrigger value="audit" data-testid="tab-audit" onClick={fetchAuditLogs}>
                <History className="w-4 h-4 mr-2" />
                Audit Logs
              </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger value="featured" data-testid="tab-featured">
                <Star className="w-4 h-4 mr-2" />
                Featured Content
              </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger value="promotions" data-testid="tab-promotions">
                <Zap className="w-4 h-4 mr-2" />
                Promotions
              </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger value="maintenance" data-testid="tab-maintenance">
                <RotateCcw className="w-4 h-4 mr-2" />
                Maintenance
              </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger value="environment" data-testid="tab-environment">
                <Shield className="w-4 h-4 mr-2" />
                Environment
              </TabsTrigger>
            )}
            {isPlatformOwner && (
              <TabsTrigger value="auto-trending" data-testid="tab-auto-trending">
                <Activity className="w-4 h-4 mr-2" />
                Auto-Trending
              </TabsTrigger>
            )}
            {hasPermission('view_reports') && (
              <TabsTrigger value="content-mgmt" data-testid="tab-content-mgmt">
                <FileText className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
            )}
            {hasPermission('view_users') && (
              <TabsTrigger value="analytics" data-testid="tab-analytics">
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {stats && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.users.total}</p>
                        <p className="text-xs text-muted-foreground">Total Users</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <Activity className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.users.active_30d}</p>
                        <p className="text-xs text-muted-foreground">Active (30d)</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <Ban className="w-8 h-8 text-red-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.users.banned}</p>
                        <p className="text-xs text-muted-foreground">Banned</p>
                      </div>
                    </div>
                  </Card>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <Crown className="w-8 h-8 text-purple-500" />
                      <div>
                        <p className="text-2xl font-bold">{stats.users.staff}</p>
                        <p className="text-xs text-muted-foreground">Staff</p>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Content Stats */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Content Overview</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xl font-bold">{stats.content.tracks}</p>
                      <p className="text-xs text-muted-foreground">Tracks</p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xl font-bold">{stats.content.videos}</p>
                      <p className="text-xs text-muted-foreground">Videos</p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xl font-bold">{stats.content.reels}</p>
                      <p className="text-xs text-muted-foreground">Clips</p>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <p className="text-xl font-bold">{stats.content.posts}</p>
                      <p className="text-xs text-muted-foreground">Posts</p>
                    </div>
                  </div>
                </Card>

                {/* Alerts */}
                {(stats.moderation.pending_reports > 0 || stats.platform.features_disabled_count > 0) && (
                  <div className="space-y-3">
                    {stats.moderation.pending_reports > 0 && (
                      <Card className="p-4 border-yellow-500/50 bg-yellow-500/10">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-yellow-500" />
                          <span>{stats.moderation.pending_reports} pending content reports need review</span>
                          <Button size="sm" variant="outline" onClick={() => setActiveTab('moderation')}>
                            Review
                          </Button>
                        </div>
                      </Card>
                    )}
                    {stats.platform.features_disabled_count > 0 && (
                      <Card className="p-4 border-orange-500/50 bg-orange-500/10">
                        <div className="flex items-center gap-3">
                          <Settings className="w-5 h-5 text-orange-500" />
                          <span>{stats.platform.features_disabled_count} features are disabled: {stats.platform.disabled_features.join(', ')}</span>
                        </div>
                      </Card>
                    )}
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="p-6">
              <div className="space-y-6">
                {/* Search */}
                <div className="flex gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by email, name, or user ID..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchUsers(userSearch)}
                      className="pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Button onClick={() => fetchUsers(userSearch)} data-testid="search-users-btn">
                    Search
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">User Email</label>
                    <Input
                      placeholder="user@email.com"
                      value={actionEmail}
                      onChange={(e) => setActionEmail(e.target.value)}
                      data-testid="action-email-input"
                    />
                  </div>
                  
                  {hasPermission('grant_tier') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Grant Tier</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedTier}
                          onChange={(e) => setSelectedTier(e.target.value)}
                          className="flex-1 p-2 rounded-md bg-background border text-sm"
                        >
                          <option value="starter">Free</option>
                          <option value="growth">Growth</option>
                          <option value="pro">Pro</option>
                          <option value="elite">Elite</option>
                          <option value="executive">Executive</option>
                          <option value="label">Label</option>
                        </select>
                        <Button
                          size="sm"
                          onClick={() => setConfirmModal({
                            isOpen: true,
                            title: 'Grant Tier',
                            message: `Grant ${selectedTier} tier to ${actionEmail}?`,
                            onConfirm: () => handleAction('grant-tier', { user_email: actionEmail, tier: selectedTier }, 'Tier granted')
                          })}
                          disabled={!actionEmail}
                          data-testid="grant-tier-btn"
                        >
                          Grant
                        </Button>
                      </div>
                    </div>
                  )}

                  {hasPermission('assign_role') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Assign Role</label>
                      <div className="flex gap-2">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value)}
                          className="flex-1 p-2 rounded-md bg-background border text-sm"
                        >
                          <option value="listener">Listener</option>
                          <option value="artist">Artist</option>
                          <option value="podcaster">Podcaster</option>
                          <option value="streamer">Streamer</option>
                          <option value="organization">Organization</option>
                        </select>
                        <Button
                          size="sm"
                          onClick={() => handleAction('assign-role', { user_email: actionEmail, role: selectedRole }, 'Role assigned')}
                          disabled={!actionEmail}
                        >
                          Set
                        </Button>
                      </div>
                    </div>
                  )}

                  {hasPermission('ban_user') && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium">User Actions</label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setConfirmModal({
                            isOpen: true,
                            title: 'Ban User',
                            message: `Are you sure you want to ban ${actionEmail}? This will prevent them from accessing the platform.`,
                            isDangerous: true,
                            onConfirm: () => handleAction('ban-user', { user_email: actionEmail }, 'User banned')
                          })}
                          disabled={!actionEmail}
                          data-testid="ban-user-btn"
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAction('unban-user', { user_email: actionEmail }, 'User unbanned')}
                          disabled={!actionEmail}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setConfirmModal({
                            isOpen: true,
                            title: 'Suspend User',
                            message: `Suspend ${actionEmail} for ${suspendHours} hours?`,
                            onConfirm: () => handleAction('suspend-user', { user_email: actionEmail, hours: suspendHours }, 'User suspended')
                          })}
                          disabled={!actionEmail}
                        >
                          <Clock className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-sm">User</th>
                        <th className="text-left p-3 text-sm">Role</th>
                        <th className="text-left p-3 text-sm">Tier</th>
                        <th className="text-left p-3 text-sm">Status</th>
                        <th className="text-left p-3 text-sm">Staff</th>
                        <th className="text-left p-3 text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-border/50 hover:bg-muted/20" data-testid={`user-row-${u.user_id}`}>
                          <td className="p-3">
                            <div>
                              <p className="font-medium">{u.full_name || u.name || 'No name'}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                              {u.handle && <p className="text-xs text-muted-foreground">{u.handle ? `@${u.handle}` : ""}</p>}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">{u.role || 'listener'}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge variant="secondary">{u.artist_tier || 'starter'}</Badge>
                          </td>
                          <td className="p-3">
                            {u.is_banned ? (
                              <Badge variant="destructive">Banned</Badge>
                            ) : u.comment_suspended_until ? (
                              <Badge className="bg-yellow-500">Suspended</Badge>
                            ) : (
                              <Badge className="bg-green-500">Active</Badge>
                            )}
                          </td>
                          <td className="p-3">
                            {u.staff_role && <StaffRoleBadge role={u.staff_role} />}
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              {hasPermission('ban_user') && !u.is_banned && u.role !== 'owner' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-yellow-500 hover:bg-yellow-500/10"
                                  onClick={() => {
                                    setActionEmail(u.email);
                                    setConfirmModal({
                                      isOpen: true,
                                      title: 'Ban User',
                                      message: `Ban ${u.full_name || u.email}? They won't be able to access the platform.`,
                                      isDangerous: true,
                                      onConfirm: () => handleAction('ban-user', { user_email: u.email }, 'User banned')
                                    });
                                  }}
                                  title="Ban user"
                                  data-testid={`ban-${u.user_id}`}
                                >
                                  <Ban className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {hasPermission('unban_user') && u.is_banned && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-green-500 hover:bg-green-500/10"
                                  onClick={() => handleAction('unban-user', { user_email: u.email }, 'User unbanned')}
                                  title="Unban user"
                                  data-testid={`unban-${u.user_id}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                </Button>
                              )}
                              {isPlatformOwner && u.role !== 'owner' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-red-500 hover:bg-red-500/10"
                                  onClick={() => setConfirmModal({
                                    isOpen: true,
                                    title: 'Delete User',
                                    message: `Delete ${u.full_name || u.email}? They will be soft-deleted and recoverable for 30 days.`,
                                    isDangerous: true,
                                    onConfirm: async () => {
                                      try {
                                        const res = await fetch(`${API}/admin/control-center/delete-user`, {
                                          method: 'DELETE',
                                          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ user_id: u.user_id, confirm_phrase: 'DELETE USER', permanent: false })
                                        });
                                        const result = await safeJson(res);
                                        if (result.ok || result.data?.success) {
                                          toast.success(`${u.full_name || u.email} deleted (recoverable for 30 days)`);
                                          fetchUsers(userSearch);
                                          fetchDeletedUsers();
                                          fetchDashboardStats();
                                        } else {
                                          toast.error(result.error || result.data?.detail || 'Delete failed');
                                        }
                                      } catch (e) { toast.error('Delete failed'); }
                                      setConfirmModal({ isOpen: false });
                                    }
                                  })}
                                  title="Delete user (recoverable 30 days)"
                                  data-testid={`delete-${u.user_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No users found. Try searching.
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* Deleted Users Section */}
            {isPlatformOwner && (
              <Card className="p-6 mt-4 border-red-500/20" data-testid="deleted-users-section">
                <div className="flex items-center justify-between mb-4">
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2 text-red-400 hover:text-red-300 p-0"
                    onClick={() => { setShowDeletedUsers(!showDeletedUsers); if (!showDeletedUsers) fetchDeletedUsers(); }}
                    data-testid="toggle-deleted-users"
                  >
                    <UserX className="w-5 h-5" />
                    <span className="font-semibold">Deleted Users ({deletedUsers.length})</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showDeletedUsers ? 'rotate-180' : ''}`} />
                  </Button>
                  {showDeletedUsers && (
                    <Button size="sm" variant="outline" onClick={fetchDeletedUsers} data-testid="refresh-deleted-users">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {showDeletedUsers && (
                  <div className="space-y-2">
                    {deletedUsers.length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-4">No deleted users.</p>
                    )}
                    {deletedUsers.map((du) => (
                      <div key={du.user_id} className="flex items-center justify-between p-3 bg-red-500/5 border border-red-500/20 rounded-lg" data-testid={`deleted-user-${du.user_id}`}>
                        <div>
                          <p className="font-medium text-sm">{du.display_name || du.name || du.email}</p>
                          <p className="text-xs text-muted-foreground">{du.email}</p>
                          <p className="text-xs text-red-400">
                            Deleted {du.deleted_at ? new Date(du.deleted_at).toLocaleDateString() : 'N/A'}
                            {du.days_remaining !== undefined && ` \u2022 ${du.days_remaining} days to recover`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-green-500 border-green-500/30 hover:bg-green-500/10"
                            onClick={() => setConfirmModal({
                              isOpen: true,
                              title: 'Restore User',
                              message: `Restore ${du.display_name || du.email}? They will be able to access the platform again.`,
                              onConfirm: () => { handleRestoreUser(du.user_id, du.email); setConfirmModal({ isOpen: false }); }
                            })}
                            data-testid={`restore-${du.user_id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-red-500 border-red-500/30 hover:bg-red-500/10"
                            onClick={() => setConfirmModal({
                              isOpen: true,
                              title: 'Permanently Delete',
                              message: `Permanently delete ${du.display_name || du.email} and ALL their data? This CANNOT be undone.`,
                              isDangerous: true,
                              onConfirm: () => { handlePurgeUser(du.user_id, du.email); setConfirmModal({ isOpen: false }); }
                            })}
                            data-testid={`purge-${du.user_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                            Purge
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </TabsContent>

          {/* Staff Tab */}
          <TabsContent value="staff">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Current Staff */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Crown className="w-5 h-5 text-purple-500" />
                  Current Staff Members
                </h3>
                <div className="space-y-3">
                  {staffMembers.map((s) => (
                    <div key={s.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium">{s.full_name || s.email}</p>
                        <p className="text-xs text-muted-foreground">{s.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StaffRoleBadge role={s.staff_role || s.role} />
                        {s.staff_role && !['owner', 'admin'].includes(s.role) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmModal({
                              isOpen: true,
                              title: 'Revoke Staff Role',
                              message: `Remove ${s.staff_role_name} role from ${s.email}?`,
                              onConfirm: () => handleAction('revoke-staff-role', { user_email: s.email, staff_role: s.staff_role }, 'Staff role revoked').then(fetchStaffMembers)
                            })}
                          >
                            <XCircle className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {staffMembers.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground">No staff members yet</p>
                  )}
                </div>
              </Card>

              {/* Grant Staff Role */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-500" />
                  Grant Staff Role
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">User Email</label>
                    <Input
                      placeholder="user@email.com"
                      value={actionEmail}
                      onChange={(e) => setActionEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Staff Role</label>
                    <select
                      value={selectedStaffRole}
                      onChange={(e) => setSelectedStaffRole(e.target.value)}
                      className="w-full p-2 rounded-md bg-background border"
                    >
                      <option value="moderator">Moderator</option>
                      <option value="featured_content_manager">Featured Content Manager</option>
                      <option value="battle_host">Battle Host</option>
                      {isOwner && <option value="admin">Admin</option>}
                    </select>
                  </div>
                  
                  {/* Role Description */}
                  {staffRoles.find(r => r.role === selectedStaffRole) && (
                    <div className="p-3 bg-muted/30 rounded-lg text-sm">
                      <p className="font-medium">{staffRoles.find(r => r.role === selectedStaffRole)?.name}</p>
                      <p className="text-muted-foreground">{staffRoles.find(r => r.role === selectedStaffRole)?.description}</p>
                      <p className="mt-2 text-xs">
                        <span className="text-muted-foreground">Permissions: </span>
                        {staffRoles.find(r => r.role === selectedStaffRole)?.permission_count} actions
                      </p>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => setConfirmModal({
                      isOpen: true,
                      title: 'Grant Staff Role',
                      message: `Grant ${selectedStaffRole.replace(/_/g, ' ')} role to ${actionEmail}?`,
                      onConfirm: () => handleAction('grant-staff-role', { user_email: actionEmail, staff_role: selectedStaffRole }, 'Staff role granted').then(fetchStaffMembers)
                    })}
                    disabled={!actionEmail}
                    className="w-full"
                    data-testid="grant-staff-role-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Grant Staff Role
                  </Button>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Platform Toggles Tab */}
          <TabsContent value="toggles">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Platform Feature Toggles
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Enable or disable platform features globally. Changes take effect immediately.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(toggles).map(([key, toggle]) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-xs text-muted-foreground">{toggle.description}</p>
                    </div>
                    <Switch
                      checked={toggle.enabled}
                      onCheckedChange={(enabled) => handleToggleChange(key, enabled)}
                      data-testid={`toggle-${key}`}
                    />
                  </div>
                ))}
              </div>
              
              {Object.keys(toggles).length === 0 && (
                <p className="text-center py-8 text-muted-foreground">Loading toggles...</p>
              )}
            </Card>
          </TabsContent>

          {/* Ads Tab */}
          <TabsContent value="ads">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-orange-500" />
                    Ad Campaign Management
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Review and approve advertising campaigns
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchPendingCampaigns}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {pendingCampaigns.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pending campaigns</p>
                  <p className="text-sm">Ad campaigns pending approval will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingCampaigns.map((campaign) => (
                    <div 
                      key={campaign.campaign_id} 
                      className="p-4 bg-muted/30 rounded-lg border border-border"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-medium">{campaign.title}</h4>
                            <Badge variant="outline">{campaign.ad_type}</Badge>
                            <Badge className="bg-yellow-500">Pending</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {campaign.description || 'No description'}
                          </p>
                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>Advertiser: {campaign.advertiser_email || campaign.advertiser_id}</span>
                            <span>Budget: {campaign.budget_amount} credits</span>
                            <span>Placements: {campaign.placements?.join(', ') || 'N/A'}</span>
                          </div>
                          {campaign.content?.headline && (
                            <p className="mt-2 text-sm italic">"{campaign.content.headline}"</p>
                          )}
                          {campaign.content?.image_url && (
                            <img 
                              src={campaign.content.image_url} 
                              alt="Ad preview" 
                              className="mt-2 max-h-32 rounded object-cover"
                            />
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button 
                            size="sm" 
                            onClick={() => setConfirmModal({
                              isOpen: true,
                              title: 'Approve Campaign',
                              message: `Approve "${campaign.title}" campaign? It will go live immediately.`,
                              onConfirm: () => { handleApproveCampaign(campaign.campaign_id); setConfirmModal({ isOpen: false }); }
                            })}
                            data-testid={`approve-${campaign.campaign_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleRejectCampaign(campaign.campaign_id)}
                            data-testid={`reject-${campaign.campaign_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Content Moderation
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Review reported content and take moderation actions.
              </p>
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No pending reports</p>
                <p className="text-sm">Content reports will appear here when users report content.</p>
              </div>
            </Card>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit">
            <Card className="p-6" data-testid="audit-logs-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Audit Logs
                  <Badge variant="secondary" className="text-xs">{auditTotal}</Badge>
                </h3>
                <div className="flex items-center gap-2">
                  <select
                    value={auditFilter.days}
                    onChange={(e) => {
                      setAuditFilter({ ...auditFilter, days: parseInt(e.target.value) });
                      setTimeout(fetchAuditLogs, 100);
                    }}
                    className="p-2 rounded-md bg-background border text-sm"
                    data-testid="audit-days-filter"
                  >
                    <option value="1">Last 24 hours</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                  <Button variant="outline" size="sm" onClick={fetchAuditLogs} data-testid="audit-refresh">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {auditLogs.map((log, idx) => {
                  const actionBadge = getAuditActionBadge(log.action);
                  return (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg text-sm" data-testid={`audit-log-${idx}`}>
                      <div className={`p-1.5 rounded ${actionBadge.bg}`}>
                        <actionBadge.icon className={`w-3.5 h-3.5 ${actionBadge.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs ${actionBadge.badgeClass}`}>
                            {actionBadge.label}
                          </Badge>
                          {log.target_email && (
                            <span className="text-xs text-muted-foreground">{log.target_email}</span>
                          )}
                          {log.target_id && !log.target_email && (
                            <span className="text-xs text-muted-foreground font-mono">{log.target_id}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          by <span className="font-medium text-foreground/80">{log.admin_email}</span> {' \u2022 '}
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1 bg-muted/30 rounded px-2 py-1">
                            {Object.entries(log.details).map(([k, v]) => (
                              <span key={k} className="mr-3">
                                <span className="text-foreground/60">{k}:</span> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {auditLogs.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No audit logs found</p>
                  </div>
                )}
              </div>
              
              {auditTotal > auditLogs.length && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  Showing {auditLogs.length} of {auditTotal} logs
                </p>
              )}
            </Card>
          </TabsContent>

          {/* Featured Content Tab - Platform Owner Only */}
          {isPlatformOwner && (
            <TabsContent value="featured">
              <FeaturedContentManager getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Promotions Tab - Platform Owner Only */}
          {isPlatformOwner && (
            <TabsContent value="promotions">
              <PromotionsManager getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Maintenance Tab - Platform Owner Only */}
          {isPlatformOwner && (
            <TabsContent value="maintenance">
              <MaintenanceTools getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Environment Debug Tab - Platform Owner Only */}
          {isPlatformOwner && (
            <TabsContent value="environment">
              <EnvironmentDebugPanel getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Auto-Trending Tab - Platform Owner Only */}
          {isPlatformOwner && (
            <TabsContent value="auto-trending">
              <AutoTrendingManager getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}

          {/* Content Management Tab */}
          {hasPermission('view_reports') && (
            <TabsContent value="content-mgmt">
              <ContentManagementPanel getAuthHeaders={getAuthHeaders} hasPermission={hasPermission} isPlatformOwner={isPlatformOwner} />
            </TabsContent>
          )}

          {/* Analytics Tab */}
          {hasPermission('view_users') && (
            <TabsContent value="analytics">
              <AnalyticsPanel getAuthHeaders={getAuthHeaders} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        isDangerous={confirmModal.isDangerous}
      />
    </div>
  );
}
